/**
 * O contrato do acompanhamento da inscrição (F06, RN-CP-30) — tipos das rotas
 * `/bff/v1/acompanhamento-inscricao`, rótulos e apresentação. Sem React.
 *
 * Fica fora de `contrato.ts` (do assistente, F05) de propósito: o
 * acompanhamento tem status que o assistente não conhece (ELEGIVEL,
 * LISTA_ESPERA) e o formato da API é outro.
 *
 * Contrato publicado em `docs/credenciamento-ponta-a-ponta/frentes/F06.md`.
 */

import {
  AlertCircle,
  Ban,
  CheckCircle2,
  Clock,
  FileCheck2,
  Hourglass,
  ListOrdered,
  RefreshCw,
  Send,
  Trophy,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

import { type StatusTone } from '@/nucleo/statusBadgeTokens';
import { type StatusVariant } from '@/ui/StatusBadge';

/*
 * Placa, telefone, CNPJ, DRE e modalidade têm UMA fonte nas telas da jornada:
 * `contrato.ts`. O acompanhamento chegou a ter a sua cópia, e ela divergiu
 * ("EXE-2B02", "TEG acessível") do assistente e do comprovante.
 */
export {
  ROTULO_DA_MODALIDADE,
  formatarCnpj,
  formatarPlaca,
  formatarTelefone,
  rotuloDaDre,
  rotuloDaModalidade,
} from './contrato';

/* ─────────────────────────────── Tipos da API ─────────────────────────────── */

export type CodigoDoProximoPasso =
  | 'AGUARDAR_TRIAGEM'
  | 'CORRIGIR'
  | 'AGUARDAR_SORTEIO'
  | 'AGUARDAR_CHAMADA'
  | 'ACESSAR_PORTAL'
  | 'ENCERRADA';

export interface ProximoPasso {
  codigo: CodigoDoProximoPasso;
  texto: string;
}

export type TipoDeEntidade = 'PF' | 'PJ' | 'COOPERATIVA';
export type CampoDeCorrecao = 'CONTATOS' | 'ESCOLAS' | 'COOPERATIVA' | 'OBSERVACAO';
export type AutorDaTransicao = 'VOCE' | 'DTP' | 'SISTEMA';

export interface VeiculoInscrito {
  protocolo: string;
  placa: string | null;
  modalidade: string | null;
  dre: { id: string; nome: string } | null;
  status: string;
  proximoPasso: ProximoPasso;
  prazo: string | null;
  atualizadoEm: string;
}

export interface EntidadeInscrita {
  submissaoId: string;
  situacao: 'RASCUNHO' | 'ENVIADA';
  ciclo: { id: string; nome: string };
  enviadaEm: string | null;
  entidade: { tipo: TipoDeEntidade; documentoMascarado: string; nome: string };
  veiculos: VeiculoInscrito[];
}

export interface TransicaoDoVeiculo {
  statusDe: string | null;
  statusPara: string;
  quando: string;
  autor: AutorDaTransicao;
  mensagem: string | null;
}

export interface PendenciaDoVeiculo {
  id: string;
  campo: CampoDeCorrecao;
  descricao: string;
  criadaEm: string;
  prazo: string | null;
}

export interface CorrecaoPedida {
  podeResponder: boolean;
  prazo: string | null;
  motivo: string | null;
  campos: CampoDeCorrecao[];
}

export interface EscolaEscolhida {
  codigoEol: string;
  nome: string;
  prioridade: number;
}

export interface DetalheDoVeiculo extends VeiculoInscrito {
  versao: string;
  ciclo: { id: string; nome: string };
  entidade: { tipo: TipoDeEntidade; documentoMascarado: string; nome: string };
  contatos: { email: string | null; telefone: string | null };
  escolas: EscolaEscolhida[];
  cooperativa: { cnpj: string; nome: string } | null;
  maxEscolas: number;
  linhaDoTempo: TransicaoDoVeiculo[];
  pendencias: PendenciaDoVeiculo[];
  correcao: CorrecaoPedida | null;
  listaEspera: { posicao: number } | null;
}

export interface OpcoesDeCorrecao {
  escolas: { codigoEol: string; nome: string; tipoUnidade: string }[];
  cooperativas: { cnpj: string; nome: string }[];
}

export interface RespostaDeCorrecao {
  versao: string;
  chaveIdempotencia: string;
  observacao: string;
  contatos?: { email: string; telefone: string };
  escolas?: string[];
  cooperativaCnpj?: string | null;
}

/* ─────────────────────────────── Apresentação ─────────────────────────────── */

export interface ApresentacaoDoStatus {
  rotulo: string;
  icone: LucideIcon;
  variante: StatusVariant;
  tom: StatusTone;
}

/**
 * Cada status do veículo inscrito, como o inscrito lê. O `tom` é o que
 * `StatusBadge` pinta para a variante — selo e superfície não discordam.
 */
export const APRESENTACAO_DO_STATUS: Record<string, ApresentacaoDoStatus> = {
  ENVIADO: { rotulo: 'Enviada', icone: Send, variante: 'pendente', tom: 'warning' },
  EM_ANALISE: { rotulo: 'Em análise', icone: Hourglass, variante: 'em_execucao', tom: 'warning' },
  AGUARDANDO_CORRECAO: {
    rotulo: 'Aguardando correção',
    icone: AlertCircle,
    variante: 'pendente',
    tom: 'warning',
  },
  REENVIADO: {
    rotulo: 'Correção enviada',
    icone: RefreshCw,
    variante: 'em_execucao',
    tom: 'warning',
  },
  ELEGIVEL: {
    rotulo: 'Apta ao sorteio',
    icone: CheckCircle2,
    variante: 'aprovado',
    tom: 'success',
  },
  LISTA_ESPERA: {
    rotulo: 'Lista de espera',
    icone: ListOrdered,
    variante: 'processado',
    tom: 'info',
  },
  PRE_CREDENCIADO: { rotulo: 'Contemplada', icone: Trophy, variante: 'ativo', tom: 'success' },
  HABILITADO: { rotulo: 'Habilitada', icone: FileCheck2, variante: 'ativo', tom: 'success' },
  REPROVADO: { rotulo: 'Não aceita', icone: XCircle, variante: 'rejeitado', tom: 'destructive' },
  DESISTENTE: { rotulo: 'Desistência', icone: Ban, variante: 'cancelado', tom: 'destructive' },
  EXPIRADO: { rotulo: 'Prazo encerrado', icone: Clock, variante: 'expirado', tom: 'neutral' },
};

const DESCONHECIDO: ApresentacaoDoStatus = {
  rotulo: 'Em andamento',
  icone: Hourglass,
  variante: 'pendente',
  tom: 'warning',
};

/** Status que este front ainda não conhece não quebra a tela: vira "Em andamento". */
export function apresentacaoDoStatus(status: string | null | undefined): ApresentacaoDoStatus {
  return (status && APRESENTACAO_DO_STATUS[status]) || DESCONHECIDO;
}

export const ROTULO_DO_TIPO_DE_ENTIDADE: Record<TipoDeEntidade, string> = {
  PF: 'Pessoa física',
  PJ: 'Pessoa jurídica',
  COOPERATIVA: 'Cooperativa',
};

export const ROTULO_DO_AUTOR: Record<AutorDaTransicao, string> = {
  VOCE: 'Você',
  DTP: 'Equipe do DTP',
  SISTEMA: 'Sistema',
};

export const ROTULO_DO_CAMPO: Record<CampoDeCorrecao, string> = {
  CONTATOS: 'Contatos',
  ESCOLAS: 'Escolas de preferência',
  COOPERATIVA: 'Cooperativa indicada',
  OBSERVACAO: 'Esclarecimento',
};

/* ─────────────────────────────── Formatadores ─────────────────────────────── */

const DATA_HORA = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** `20/10/2026 às 18:00`, no fuso de São Paulo; travessão quando não há data. */
export function formatarDataHora(valor: string | null | undefined): string {
  if (!valor) return '—';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '—';
  return DATA_HORA.format(data).replace(',', ' às');
}

/** Caminho do detalhe de um veículo. */
export function caminhoDoVeiculo(protocolo: string): string {
  return `/credenciados/acompanhamento/${encodeURIComponent(protocolo)}`;
}

/** Portal do Credenciado (Keycloak) — o pré-credenciado entra com a conta criada por F09. */
export const CAMINHO_DO_PORTAL_DO_CREDENCIADO = '/credenciado';
export const CAMINHO_PARA_ENTRAR = '/credenciados/entrar';
/** "Minhas inscrições" — a lista do acompanhamento (F06). */
export const CAMINHO_DO_ACOMPANHAMENTO = '/credenciados/acompanhamento';
export const CAMINHO_DO_AUTOCADASTRO = '/credenciados/autocadastro';
/**
 * Consulta anônima por protocolo e CPF ou CNPJ (J-02/S04). Não pede conta nem
 * sessão, e é por isso que continua viva ao lado do acompanhamento com sessão
 * (decisão do dono, D4 de 2026-09-30): o critério de aceite da J-02 exige que
 * quem só tem o número do protocolo consiga ver o andamento resumido.
 */
export const CAMINHO_DA_CONSULTA_POR_PROTOCOLO = '/jornadas/j02/s04-acompanhar-protocolo';
